import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { DropzoneStatus } from "v2/components/entityDocuments/Dropzone";
import {
  entityDocumentsApi,
  POST_UPLOAD_TAGS,
  RequiredItem,
  useGetEntityOverviewQuery,
  useGetProcessingStatusQuery,
} from "v2/store/entityDocumentsApi";

type UseDocumentUploadFlowArgs = {
  entityId: string;
  items: RequiredItem[];
  onUploadFiles: (files: File[]) => Promise<string[]>;
};

export type DocumentUploadFlow = {
  status: DropzoneStatus;
  matchedCount: number;
  isUploading: boolean;
  matchedIds: Set<string>;
  visibleItems: RequiredItem[];
  handleFiles: (files: File[]) => void;
};

const DONE_RESET_MS = 4000;
const PROCESSING_POLL_MS = 2000;

const TICK_HOLD_MS = 700;

export const useDocumentUploadFlow = ({
  entityId,
  items,
  onUploadFiles,
}: UseDocumentUploadFlowArgs): DocumentUploadFlow => {
  const { isCustomer } = useRoleBasedView();

  const [parseStatus, setParseStatus] = useState<DropzoneStatus>("idle");
  const [matchedCount, setMatchedCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const timeouts = useRef<ReturnType<typeof setTimeout>[]>([]);
  const scheduledRemovalRef = useRef<Set<string>>(new Set());

  const dispatch = useDispatch();

  useGetEntityOverviewQuery(
    { entityId },
    { pollingInterval: isProcessing ? PROCESSING_POLL_MS : 0 }
  );

  const { data: processingStatus, isFetching: isFetchingStatus } =
    useGetProcessingStatusQuery(
      { entityId, fileIds: uploadedFileIds },
      {
        skip: !isProcessing || uploadedFileIds.length === 0,
        pollingInterval: PROCESSING_POLL_MS,
        refetchOnMountOrArgChange: true,
      }
    );

  useEffect(() => {
    const pending = timeouts.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (!isProcessing || isFetchingStatus || !processingStatus?.allDone) return;

    setIsProcessing(false);
    setParseStatus("done");
    dispatch(entityDocumentsApi.util.invalidateTags([...POST_UPLOAD_TAGS]));
    timeouts.current.push(
      setTimeout(() => setParseStatus("idle"), DONE_RESET_MS)
    );
  }, [isProcessing, isFetchingStatus, processingStatus?.allDone, dispatch]);

  const matchedIds = new Set(processingStatus?.matchedDocumentIds ?? []);
  const matchedDocumentIds = processingStatus?.matchedDocumentIds;

  useEffect(() => {
    (matchedDocumentIds ?? []).forEach((id) => {
      if (scheduledRemovalRef.current.has(id)) return;

      scheduledRemovalRef.current.add(id);
      timeouts.current.push(
        setTimeout(() => {
          setRemovedIds((prev) => new Set(prev).add(id));
        }, TICK_HOLD_MS)
      );
    });
  }, [matchedDocumentIds]);

  const visibleItems = items.filter((item) => {
    if (removedIds.has(item.id) || item.isSatisfied) return false;
    return isCustomer ? item.status == null : true;
  });

  const runUpload = async (files: File[]): Promise<void> => {
    const wasProcessing = parseStatus === "loading";
    setParseStatus("loading");
    try {
      const uploadedIds = await onUploadFiles(files);
      setMatchedCount((prev) => (wasProcessing ? prev : 0) + files.length);

      if (uploadedIds.length === 0) {
        if (!wasProcessing) setParseStatus("idle");
        return;
      }

      setUploadedFileIds((prev) =>
        wasProcessing ? [...prev, ...uploadedIds] : uploadedIds
      );

      setIsProcessing(true);
    } catch {
      if (!wasProcessing) setParseStatus("idle");
    }
  };

  const handleFiles = (files: File[]): void => void runUpload(files);

  return {
    status: parseStatus,
    matchedCount,
    isUploading: parseStatus === "loading" || isProcessing,
    matchedIds,
    visibleItems,
    handleFiles,
  };
};
